import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { computed, flow, observable, runInAction } from "mobx";
import { EProductPlanPaymentInterval, ESubscriptionStatus, routeLocationLicensePost, routeLocationSubscriptionChangePlanGet, routeLocationSubscriptionsDelete } from "@devowl-wp/api-real-commerce";
import { RealCommerceError } from "../../exceptions/realCommerceError.js";
import { localizePaymentInterval } from "../../others/localizePaymentInterval.js";
import { formatNumber } from "../../utils/getDecimalSeparator.js";
import { getLegalManufacturerUrls, getLegalSellerUrls } from "../../utils/legalEntities.js";
import { realCommerceRequest } from "../../utils/realCommerceRequest.js";
import { ChangePlan } from "../changePlan.js";
import { License } from "../license.js";
class Subscription {
    get isOneTime() {
        return this.data.productPlan.paymentInterval === EProductPlanPaymentInterval.OneTime;
    }
    get humanReadablePaymentMethod() {
        return this.getHumanReadablePaymentMethod();
    }
    get canBeModified() {
        return !!this.data.ecommerceSystemSubscriptionId;
    }
    get isLifetime() {
        return !this.data.nextInvoicing && this.data.status === ESubscriptionStatus.Active;
    }
    get isInstallableProduct() {
        return this.installations?.length > 0;
    }
    get installations() {
        return this.store.installations.get(this.data.id);
    }
    get productVariants() {
        return this.store.rpmProductVariantToSubscription.get(this.data.id);
    }
    get licenses() {
        return Array.from(this.store.licenses.values()).filter((l)=>l.data.subscription.id === this.data.id);
    }
    get labelPrice() {
        return formatNumber(+this.data.productPlan.price[0].recurringPrice);
    }
    get labelPaymentInterval() {
        return localizePaymentInterval(this.data.productPlan.paymentInterval);
    }
    get seller() {
        return getLegalSellerUrls();
    }
    get manufacturer() {
        return getLegalManufacturerUrls();
    }
    constructor(data, store){
        this.busy = false;
        /**
     * Save the last applied coupon of this transaction. This is necessary so we
     * can automatically safe the coupon when using for changing (upgrading) the plan.
     */ this.lastAppliedCoupon = "";
        this.changePlan = [];
        this.cancel = flow(function*({ cancellationReason }) {
            this.busy = true;
            yield this.store.probablyRenewJwt();
            try {
                yield realCommerceRequest({
                    location: routeLocationSubscriptionsDelete,
                    request: {
                        subscription: {
                            id: this.data.id,
                            cancellationReason
                        }
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                // Reload all subscriptions
                this.store.fetchSubscriptions();
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busy = false;
            }
        });
        this.fetchChangePlans = flow(function*(coupon) {
            this.busy = true;
            yield this.store.probablyRenewJwt();
            try {
                const result = yield realCommerceRequest({
                    location: routeLocationSubscriptionChangePlanGet,
                    params: {
                        subscriptionId: this.data.id,
                        coupon
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                this.changePlan = [];
                for (const row of result.options){
                    this.changePlan.push(new ChangePlan(row, this));
                }
                this.lastAppliedCoupon = coupon;
            } catch (e) {
                console.log(e);
                this.lastFetchPlanError = new RealCommerceError(e);
                throw this.lastFetchPlanError;
            } finally{
                this.busy = false;
            }
        });
        this.addLicense = flow(function*({ rpmProductVariantId, ...rest }) {
            yield this.store.probablyRenewJwt();
            try {
                const { license } = yield realCommerceRequest({
                    location: routeLocationLicensePost,
                    request: {
                        subscription: {
                            id: this.data.id
                        },
                        productPlanRpmProductVariant: {
                            rpmProductVariantId
                        },
                        license: rest
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                this.store.licenses.set(license.id, new License(license, this.store));
                // Recalculate "Products" so "usage / maxUsage" count is corrected
                this.store.fetchSubscriptions();
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            }
        });
        this.store = store;
        runInAction(()=>{
            this.data = data;
        });
    }
}
_ts_decorate([
    observable
], Subscription.prototype, "busy", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Number)
], Subscription.prototype, "sortIndex", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Subscription.prototype, "data", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof RealCommerceError === "undefined" ? Object : RealCommerceError)
], Subscription.prototype, "lastFetchPlanError", void 0);
_ts_decorate([
    observable
], Subscription.prototype, "lastAppliedCoupon", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], Subscription.prototype, "changePlan", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isOneTime", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "humanReadablePaymentMethod", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "canBeModified", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isLifetime", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isInstallableProduct", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "installations", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "productVariants", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "licenses", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "labelPrice", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "labelPaymentInterval", null);
export { Subscription };
